"use client"

import { useId } from "react"
import { useField } from "@payloadcms/ui"
import type { TextareaFieldClientComponent } from "payload"

import { Textarea } from "@/components/ui/textarea"
import { FieldShell, resolveLabel } from "./field-shell"

/**
 * Shadcn-themed Payload Textarea field. Same pattern as the text
 * field — wraps the storefront `<Textarea>` primitive in
 * `useField()` so Payload's form-state pipeline still drives drafts
 * + autosave + validation.
 */
export const ShadcnTextareaField: TextareaFieldClientComponent = (props) => {
  const { path, field } = props
  const fieldName = (field as { name?: string })?.name ?? path
  const { value, setValue, showError, errorMessage } = useField<string>({
    path: path ?? fieldName,
  })
  const id = useId()

  const label = resolveLabel(
    (field as { label?: unknown }).label,
    fieldName,
  )
  const description =
    (field as { admin?: { description?: unknown } })?.admin?.description
  const placeholder =
    ((field as { admin?: { placeholder?: unknown } })?.admin?.placeholder as
      | string
      | undefined) ?? undefined
  const required = Boolean((field as { required?: boolean }).required)
  const rows =
    ((field as { admin?: { rows?: number } })?.admin?.rows as number | undefined) ?? 4
  const maxLength = (field as { maxLength?: number })?.maxLength

  return (
    <FieldShell
      label={label}
      required={required}
      description={
        typeof description === "string" || typeof description === "object"
          ? (description as React.ReactNode)
          : undefined
      }
      error={errorMessage}
      showError={showError}
      htmlFor={id}
    >
      <Textarea
        id={id}
        value={typeof value === "string" ? value : ""}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={rows}
        aria-invalid={showError ? true : undefined}
        className="rounded-xl min-h-[88px]"
      />
    </FieldShell>
  )
}

export default ShadcnTextareaField
